import { Component, ElementRef, EventEmitter, forwardRef, HostListener, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { concat, Observable, of, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { Guid, setTimeout$, Utility } from 'src/app/core/common';
import { SharedService } from 'src/app/services/shared.service';

@Component({
  selector: 'app-autocomplete',
  templateUrl: './autocomplete.component.html',
  styleUrls: ['./autocomplete.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => AutocompleteComponent),
    multi: true,
  },
  {
    provide: NG_VALIDATORS,
    useExisting: forwardRef(() => AutocompleteComponent),
    multi: true,
  }]
})
export class AutocompleteComponent implements OnInit, ControlValueAccessor, Validator {
  Utility = Utility;
  constructor(private element: ElementRef<HTMLElement>,
    private sharedService: SharedService) {
    this.required = this.element.nativeElement.hasAttribute('required');
    if (!this.searchFn)
      this.searchFn = (term: string) => this.sharedService.getSuggestions(this.searchSource, term, this.maxSuggest, this.searchTypes);
  }

  popup: boolean = false;
  required: boolean;
  @Input() disabled: boolean;
  items$: Observable<{ term: string, type: string }[]>;
  searchInput$ = new Subject<string>();
  @Input() searchSource: string = 'jobtitle';
  @Input() searchTypes: string[] = [];
  @Input() defaultItems: string[] = [];
  @Input() searchFn: (term: string) => Observable<{ Item1: string, Item2: string }[]>;
  @Input() placeholder: string;
  @Input() maxLength = 100;
  @Input() typeToSearchText: string = "لطفا عبارت مورد نظر را جستجو کنید";
  @Input() createItemText: string = "جستجوی";
  @Input() notFoundText: string = "";
  @Input('max-suggest') maxSuggest = 10;
  @Input() searchIcon = false;
  @Input() clearIcon = false;
  @Input('appendResultsTo') popupSelector: string = undefined;
  @Output() change = new EventEmitter<string>();
  @Output() selected = new EventEmitter<string>();
  popupId = 'popup-' + Guid.newGuid();

  ngOnInit(): void {
    if (Utility.mobile)
      this.popupSelector = '#' + this.popupId;

    this.setItemsFilter();
  }

  @ViewChild('selector') selector: NgSelectComponent;
  @HostListener('focus') focus() {
    this.selector?.open();
  }
  setTerm(value?: string, event?: Event) {
    event?.stopPropagation();
    if (value) this.selector.searchTerm = value + ' ';
    else this.selector.handleClearClick();
    return false;
  }

  enter(selected?: any) {
    const item = selected || <any>this.selector.itemsList.items.find(_ => _.selected)?.value;
    if (item) this.value = item?.term;
    this.popup = false;
    this.selected.emit(item?.id || this.value);
  }

  _value: string;
  get value(): string {
    return this._value || undefined;
  }
  set value(val: string) {
    if ((!val && !this._value) || val == this._value) return;
    this._value = val;
    this.propagateChange(val);
    this.change.emit(val);
    if (!val) this.selected.emit(null);
  }

  validate(control: AbstractControl): ValidationErrors {
    if (!this.required || this._value) {
      return null;
    }
    return { required: true };
  }
  writeValue(value: string): void {
    this.value = value || '';
  }
  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }
  registerOnTouched(fn: any): void { }
  private propagateChange = (_: any) => { };

  setItemsFilter() {
    // code from ng-select "search and autocomplete" example:
    //  https://stackblitz.com/run?file=src%2Fsearch-autocomplete-example.component.ts
    //  https://ng-select.github.io/ng-select#/search
    this.items$ = concat(
      of(this.defaultItems), //default items
      this.searchInput$.pipe(
        distinctUntilChanged(),
        switchMap(term => {
          this.value = term;
          return concat(
            of([{ term: term, id: term, type: 'search' }]),
            this.searchFn(term)
              .pipe(
                map(_ => Utility.concatUnique([{ term: term, id: term, type: 'search' }], _.map(t => { return <any>{ term: t.Item1, id: t.Item2, type: 'suggestion' }; }))),
                catchError(() => of(this.defaultItems)), // empty list on error
              )

          );
        }
        )
      )
    );
  }

  openPopup() {
    this.popup = true;
    setTimeout$(() => { this.selector?.open(); }, 100);

    window.onpopstate = () => this.closePopup();
    history.pushState('modal-opened', null);
  }

  closePopup() {
    this.popup = false;

    window.onpopstate = null;
    history.replaceState('modal-closed', null);
  }

}
